import { WithRouter, withRouter } from 'found';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { fetchCollectionContents, fetchCollections } from '../actions/collections.actions';
import withAsyncData from '../components/utils/withAsyncData';
import { routes } from '../constants';
import { StoreType } from '../reducers';
import { Collection, Pagination } from '../types/AppContent';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
  selectCollectionWithContents,
  selectCollectionStatus,
  CollectionTypes,
} from '../reducers/collections.selectors';
import { createPaginationRequest } from '../utils/pagination';
import CuratedCollectionOverview from '../components/CuratedCollectionOverview';

interface DispatchProps {
  fetchCollectionContents: typeof fetchCollectionContents;
}

interface OwnProps {
  match: {
    params: {
      collectionId: string;
    };
  };
}

interface StateProps {
  collection?: Collection;
  collectionStatus: AsyncActionStatus;
  collectionPagination: Pagination;
}

type FollowedCollectionItemListPageProps = DispatchProps & StateProps & WithRouter & OwnProps;

const FollowedCollectionItemListPage: React.FC<FollowedCollectionItemListPageProps> = ({
  match,
  collection,
  collectionStatus,
  router,
  ...props
}) => {
  const collectionId = match.params.collectionId;

  useEffect(() => {
    props.fetchCollectionContents(collectionId, createPaginationRequest(props.collectionPagination));
  }, [collectionId]);

  if (!collection && collectionStatus === AsyncActionStatus.NOTFOUND) {
    router.replace(`/${routes.curatedCollections.root}/${collectionId}`);
    return null;
  }
  if (!collection || !collection.isFollowing) {
    return null;
  }

  return (
    <CuratedCollectionOverview
      collection={collection}
      collectionStatus={collectionStatus}
      collectionId={collectionId}
      collectionPagination={props.collectionPagination}
      loadNext={() => {
        return props.fetchCollectionContents(collectionId, createPaginationRequest({
          ...props.collectionPagination,
          offset: props.collectionPagination.offset + props.collectionPagination.limit,
        }));
      }}
    />
  );
};

const mapState = (state: StoreType, props: OwnProps): StateProps => ({
  collection: selectCollectionWithContents(state, props.match.params.collectionId, CollectionTypes.FOLLOWED),
  collectionStatus: selectCollectionStatus(state, props.match.params.collectionId, CollectionTypes.FOLLOWED),
  collectionPagination: state.collections.byId[props.match.params.collectionId] &&
    state.collections.byId[props.match.params.collectionId].pagination,
});

export default compose<React.FC<Record<string,never>>>(
  withRouter,
  connect(
    mapState,
    { fetchCollections, fetchCollectionContents },
  ),
  withAsyncData((props) => ({
    refetch: () => props.fetchCollections(),
    dataProp: 'collection',
    statusProp: 'collectionStatus'
  })),
)(FollowedCollectionItemListPage);
